import React, { useMemo, useState } from "react";
import { Download } from "lucide-react";
import benchmark from "../data/benchmark.json";

const tasks = ["all", ...Array.from(new Set(benchmark.map((r) => r.task)))];
const taskLabel = (s: string) =>
  s === "all"
    ? "All tasks"
    : s.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());
export const Benchmark: React.FC = () => {
  const [task, setTask] = useState("all");
  const [sort, setSort] = useState("score");
  const [selected, setSelected] = useState<string | null>(null);
  const rows = useMemo(() => {
    const groups = new Map<string, typeof benchmark>();
    benchmark
      .filter((r) => task === "all" || r.task === task)
      .forEach((r) => groups.set(r.model, [...(groups.get(r.model) || []), r]));
    return Array.from(groups, ([model, values]) => ({
      model,
      label: model.split("/").pop()!,
      score: values.reduce((a, r) => a + r.score, 0) / values.length,
      latency: values.reduce((a, r) => a + r.latency, 0) / values.length,
      n: values.length,
    })).sort((a, b) =>
      sort === "score" ? b.score - a.score : a.latency - b.latency,
    );
  }, [task, sort]);
  const chosen = rows.find((r) => r.model === selected) || rows[0];
  const maxLatency = Math.ceil(Math.max(...rows.map((r) => r.latency)));
  return (
    <section className="benchmark" aria-labelledby="benchmark-heading">
      <div className="benchmark-heading">
        <div>
          <p className="project-meta">Explore the recorded experiment</p>
          <h2 id="benchmark-heading">Comparing models by task.</h2>
        </div>
        <a className="text-link" href="/data/smarteats-benchmark.csv" download>
          Download data <Download size={16} aria-hidden="true" />
        </a>
      </div>
      <p>
        Compare average recorded self-evaluation score with response latency.
        Choose a task, then select a model in the chart or table.
      </p>
      <div className="benchmark-controls">
        <label>
          Task
          <select
            value={task}
            onChange={(e) => {
              setTask(e.target.value);
              setSelected(null);
            }}
          >
            {tasks.map((t) => (
              <option key={t} value={t}>
                {taskLabel(t)}
              </option>
            ))}
          </select>
        </label>
        <label>
          Table order
          <select value={sort} onChange={(e) => setSort(e.target.value)}>
            <option value="score">Highest score</option>
            <option value="latency">Lowest latency</option>
          </select>
        </label>
        <span>
          {task === "all"
            ? "375 evaluations / 25 per model"
            : "75 evaluations / 5 per model"}
        </span>
      </div>
      <div className="chart-layout">
        <div className="scatter-wrap">
          <span className="axis-label">Mean score / 10 ↑</span>
          <div className="scatter">
            <div className="grid-lines" aria-hidden="true">
              {[10, 7.5, 5, 2.5, 0].map((n) => (
                <span key={n}>{n}</span>
              ))}
            </div>
            {rows.map((r) => (
              <button
                key={r.model}
                className={
                  chosen.model === r.model ? "plot-dot active-dot" : "plot-dot"
                }
                style={{
                  left: `${(r.latency / maxLatency) * 100}%`,
                  bottom: `${r.score * 10}%`,
                }}
                aria-label={`${r.label}: score ${r.score.toFixed(2)}, latency ${r.latency.toFixed(2)} seconds`}
                aria-pressed={chosen.model === r.model}
                onClick={() => setSelected(r.model)}
                title={r.label}
              />
            ))}
          </div>
          <div className="x-axis">
            <span>0 s</span>
            <span>Mean latency →</span>
            <span>{maxLatency} s</span>
          </div>
        </div>
        <div className="model-detail" aria-live="polite">
          <span>Selected model</span>
          <h3>{chosen.label}</h3>
          <dl>
            <div>
              <dt>Mean score</dt>
              <dd>
                {chosen.score.toFixed(2)}
                <small> / 10</small>
              </dd>
            </div>
            <div>
              <dt>Mean latency</dt>
              <dd>
                {chosen.latency.toFixed(2)}
                <small> s</small>
              </dd>
            </div>
            <div>
              <dt>Evaluations</dt>
              <dd>{chosen.n}</dd>
            </div>
          </dl>
          <p>{taskLabel(task)}. Arithmetic means from the recorded runs.</p>
        </div>
      </div>
      <details className="benchmark-table">
        <summary>Compare all 15 models in a table</summary>
        <div className="table-scroll">
          <table>
            <caption>
              {taskLabel(task)}: recorded model evaluation averages
            </caption>
            <thead>
              <tr>
                <th scope="col">Model</th>
                <th scope="col">Score / 10</th>
                <th scope="col">Latency (s)</th>
                <th scope="col">Runs</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.model}>
                  <th scope="row">
                    <button
                      onClick={() => setSelected(r.model)}
                      aria-pressed={chosen.model === r.model}
                    >
                      {r.label}
                    </button>
                  </th>
                  <td>{r.score.toFixed(2)}</td>
                  <td>{r.latency.toFixed(2)}</td>
                  <td>{r.n}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
      <p className="method-note">
        Source: SmartEats A7 experiment. Scores come from the recorded
        self-evaluation rubric. The small prompt sets and run-specific latency
        limit generalization. These are historical experiment results.
      </p>
    </section>
  );
};
